/**
 * Real backend adapter. Targets the Django API from the plan.
 *
 * Carries forward the JWT refresh fix learned the hard way in ROS: a rotated
 * refresh token blacklists concurrent tabs and causes random logouts. The
 * single-flight mutex below ensures only ONE refresh is ever in flight and
 * every waiting caller reuses its result.
 */
import type { PinchcrewApi, ShiftFilters } from './client'
import { ApiRequestError } from './client'
import type {
  Assignment, AttendanceRecord, BoardState, Certificate, Course, DocType, Module, Page,
  Payout, Question, Rating, ShiftPost, Site, Timesheet, VettingStage, Worker,
  WorkerDocument, WorkerSkill,
  OnboardingStatus, ScreeningQuestion, OrientationSection, RequiredDocument,
  BankDetails, ReferralSummary,
} from './types'

const TOKEN_KEY = 'pinchcrew.worker.token'
const REFRESH_KEY = 'pinchcrew.worker.refresh'

function readToken(key: string): string | null {
  try { return localStorage.getItem(key) } catch { return null }
}
function writeToken(key: string, value: string | null): void {
  try { value === null ? localStorage.removeItem(key) : localStorage.setItem(key, value) } catch { /* private mode */ }
}

export class HttpApi implements PinchcrewApi {
  /** Single-flight refresh — see class doc. */
  private refreshInFlight: Promise<string> | null = null

  constructor(private readonly baseUrl: string) {}

  private async request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
    const token = readToken(TOKEN_KEY)
    const headers = new Headers(init.headers)
    if (token) headers.set('Authorization', `Bearer ${token}`)
    if (!(init.body instanceof FormData)) headers.set('Content-Type', 'application/json')

    const res = await fetch(`${this.baseUrl}${path}`, { ...init, headers })

    if (res.status === 401 && retry && readToken(REFRESH_KEY)) {
      await this.refresh()
      return this.request<T>(path, init, false)
    }

    if (!res.ok) {
      let body: { code?: string; message?: string; field_errors?: Record<string, string[]> } = {}
      try { body = await res.json() } catch { /* non-JSON error body */ }
      throw new ApiRequestError(
        body.code ?? 'http_error',
        body.message ?? `Request failed (${res.status})`,
        body.field_errors,
        res.status,
      )
    }

    if (res.status === 204) return undefined as T
    return res.json() as Promise<T>
  }

  private refresh(): Promise<string> {
    if (this.refreshInFlight) return this.refreshInFlight

    this.refreshInFlight = (async () => {
      const refresh = readToken(REFRESH_KEY)
      const res = await fetch(`${this.baseUrl}/auth/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh }),
      })
      if (!res.ok) {
        writeToken(TOKEN_KEY, null)
        writeToken(REFRESH_KEY, null)
        throw new ApiRequestError('session_expired', 'Please sign in again.', undefined, 401)
      }
      const data = await res.json() as { access: string; refresh?: string }
      writeToken(TOKEN_KEY, data.access)
      if (data.refresh) writeToken(REFRESH_KEY, data.refresh)
      return data.access
    })().finally(() => { this.refreshInFlight = null })

    return this.refreshInFlight
  }

  private post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined })
  }

  // ---- auth ----
  requestOtp(phone: string) {
    return this.post<{ sent_to: string; expires_in: number }>('/auth/otp/request/', { phone })
  }

  async verifyOtp(phone: string, code: string) {
    const data = await this.post<{ access: string; refresh: string; worker: Worker; is_new: boolean }>(
      '/auth/otp/verify/', { phone, code },
    )
    writeToken(TOKEN_KEY, data.access)
    writeToken(REFRESH_KEY, data.refresh)
    return data
  }

  /** True when a token survives a reload, so the app can restore the session. */
  hasStoredSession(): boolean {
    return readToken(TOKEN_KEY) !== null
  }

  async logout() {
    writeToken(TOKEN_KEY, null)
    writeToken(REFRESH_KEY, null)
  }

  // ---- profile & vetting ----
  getMe() { return this.request<Worker>('/workers/me/') }
  updateMe(patch: Partial<Worker>) {
    return this.request<Worker>('/workers/me/', { method: 'PATCH', body: JSON.stringify(patch) })
  }
  getMySkills() { return this.request<WorkerSkill[]>('/workers/me/skills/') }
  getMyDocuments() { return this.request<WorkerDocument[]>('/workers/me/documents/') }
  uploadDocument(docType: DocType, file: File) {
    const fd = new FormData()
    fd.append('doc_type', docType)
    fd.append('file', file)
    return this.request<WorkerDocument>('/workers/me/documents/', { method: 'POST', body: fd })
  }
  getVettingStages() { return this.request<VettingStage[]>('/vetting/me/') }

  // ---- progressive onboarding ----
  getOnboardingStatus() { return this.request<OnboardingStatus>('/onboarding/status/') }
  getQuestionnaire() {
    return this.request<{ questions: ScreeningQuestion[]; answers: Record<string, unknown>; submitted: boolean }>(
      '/onboarding/questionnaire/',
    )
  }
  submitQuestionnaire(answers: Record<string, unknown>) {
    return this.post<{ status: string }>('/onboarding/questionnaire/submit/', { answers })
  }
  getOrientation() {
    return this.request<{ version: string; sections: OrientationSection[]; acknowledged: boolean }>(
      '/onboarding/orientation/',
    )
  }
  acknowledgeOrientation() {
    return this.post<{ acknowledged: boolean; stages_passed: string[] }>(
      '/onboarding/orientation/acknowledge/',
    )
  }
  getRequiredDocuments() { return this.request<RequiredDocument[]>('/onboarding/documents/') }
  getBankDetails() { return this.request<BankDetails>('/workers/me/bank/') }
  saveBankDetails(input: { bank_name: string; account_no: string; account_holder: string }) {
    return this.post<BankDetails>('/workers/me/bank/', input)
  }
  getReferrals() { return this.request<ReferralSummary>('/workers/me/referrals/') }
  useReferralCode(code: string) {
    return this.post<{ attached: boolean; referrer_name: string | null; your_bonus: string | null }>(
      '/workers/me/referrals/use/', { code },
    )
  }

  // ---- shifts ----
  browseShifts(filters: ShiftFilters = {}) {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(filters)) if (v !== undefined) q.set(k, String(v))
    return this.request<Page<ShiftPost> & { board?: BoardState }>(`/shifts/?${q}`)
  }
  getShift(id: string) { return this.request<ShiftPost>(`/shifts/${id}/`) }
  claimShift(id: string) { return this.post<Assignment>(`/shifts/${id}/claim/`) }
  async cancelClaim(assignmentId: string, reason?: string) {
    await this.post(`/assignments/${assignmentId}/cancel/`, { reason })
  }
  getMyAssignments(scope: 'upcoming' | 'past' = 'upcoming') {
    return this.request<Assignment[]>(`/assignments/?scope=${scope}`)
  }

  // ---- attendance ----
  clockIn(assignmentId: string, qrPayload: string, coords?: GeolocationCoordinates) {
    return this.post<AttendanceRecord>(`/assignments/${assignmentId}/clock-in/`, {
      qr: qrPayload, lat: coords?.latitude, lng: coords?.longitude, accuracy: coords?.accuracy,
    })
  }
  clockOut(assignmentId: string, qrPayload: string, coords?: GeolocationCoordinates) {
    return this.post<AttendanceRecord>(`/assignments/${assignmentId}/clock-out/`, {
      qr: qrPayload, lat: coords?.latitude, lng: coords?.longitude, accuracy: coords?.accuracy,
    })
  }
  startBreak(assignmentId: string) { return this.post<AttendanceRecord>(`/assignments/${assignmentId}/break-start/`) }
  endBreak(assignmentId: string) { return this.post<AttendanceRecord>(`/assignments/${assignmentId}/break-end/`) }

  // ---- money ----
  getTimesheets() { return this.request<Timesheet[]>('/timesheets/') }
  getPayouts() { return this.request<Payout[]>('/payouts/') }
  disputeTimesheet(id: string, reason: string, claimedMinutes: number) {
    return this.post<Timesheet>(`/timesheets/${id}/dispute/`, { reason, claimed_minutes: claimedMinutes })
  }

  // ---- learning ----
  getCourses() { return this.request<Course[]>('/courses/') }
  getCourse(slug: string) { return this.request<{ course: Course; modules: Module[] }>(`/courses/${slug}/`) }
  completeModule(courseSlug: string, moduleId: string) {
    return this.post<Course>(`/courses/${courseSlug}/modules/${moduleId}/complete/`)
  }
  getQuiz(courseSlug: string) { return this.request<Question[]>(`/courses/${courseSlug}/quiz/`) }
  submitQuiz(courseSlug: string, answers: Record<string, string>) {
    return this.post<{ passed: boolean; score: number; certificate?: Certificate }>(
      `/courses/${courseSlug}/quiz/submit/`, { answers },
    )
  }
  getCertificates() { return this.request<Certificate[]>('/certificates/') }

  // ---- reputation ----
  getMyRatings() { return this.request<Rating[]>('/ratings/me/') }
  getSites() { return this.request<Site[]>('/sites/') }
}
